// adminSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase.config"; // Adjust this import based on your Firebase setup
import { buildFullTenantModules } from "../AdminDashboard/tenantModulesCatalog";

// Async Thunks
export const createUser = createAsyncThunk(
  "admin/createUser",
  async (userData, thunkAPI) => {
    try {
      const createUserFunction = httpsCallable(
        functions,
        "adminDashboardF-createUser"
      );
      const result = await createUserFunction(userData);
      if (result.data.success) {
        alert(
          `User created successfully. Verification email sent to ${userData.email}`
        );
        return result.data;
      }
      // A non-throwing failure used to fall off the end of the try and
      // FULFIL with undefined (SettingsTab then cleared the email box and
      // showed nothing) - reject it like createCustomer does.
      return thunkAPI.rejectWithValue("Failed to add user. Please try again.");
    } catch (err) {
      console.error("Error adding user: ", err);
      // Callable HttpsError messages are authored server-side (the new
      // role/email validations in adminDashboardF-createUser, or a
      // duplicate-email Auth failure) - surface them like createCustomer
      // does above, instead of the generic sentence that used to hide them.
      return thunkAPI.rejectWithValue(
        err?.message ? `Failed to add user: ${err.message}` : "Failed to add user. Please try again."
      );
    }
  }
);

export const createCustomer = createAsyncThunk(
  "admin/createCustomer",
  async (customerData, thunkAPI) => {
    try {
      const createCustomerFunction = httpsCallable(
        functions,
        "adminDashboardF-createCustomer"
      );
      const result = await createCustomerFunction(customerData);
      if (result.data.success) {
        alert(
          `Customer ${customerData.name} created successfully. Verification email sent.`
        );
        // id = the new customer's uid, so the fulfilled reducer can show the
        // row at once (fetchCustomers only runs on mount, and an immediate
        // refetch can lose the metadata/customers.lastUpdated race).
        // emailVerified/createdAt are stand-ins for what the server actually
        // wrote (see adminDashboardF-createCustomer) so the new row shows
        // "Invited" + today immediately, instead of blank/unknown until the
        // next fetchCustomers. `modules` mirrors DEFAULT_NEW_CUSTOMER_MODULES
        // in functions/allFunctions/shared/tenantModules.js (base product ON,
        // today's paid add-ons OFF) - without it, addOnSummary(undefined)
        // normalizes to "everything on" and the Modules card/"Manage modules"
        // checkboxes would assert the opposite of what the server just wrote.
        const seededModules = buildFullTenantModules();
        return {
          ...customerData,
          id: result.data.customerId,
          emailVerified: false,
          createdAt: new Date().toISOString(),
          modules: {
            // fleet/fleetCan added (fix round 2026-09-24, Opus review B7):
            // DEFAULT_NEW_CUSTOMER_MODULES.web turns both off for a new
            // customer same as warehouse - without them here the optimistic
            // row showed Fleet GPS/CAN odometer as ON until the next
            // fetchCustomers overwrote it with the server's real doc.
            web: { ...seededModules.web, warehouse: false, fleet: false, fleetCan: false },
            mobile: { ...seededModules.mobile, warehouse: false },
            restricted: { ...seededModules.restricted, gc: false },
          },
        };
      }
      return thunkAPI.rejectWithValue("Failed to add customer. Please try again.");
    } catch (err) {
      console.error("Error adding customer: ", err);
      // Callable HttpsError messages are authored server-side
      // ("email is required.", "Error creating new customer.") - surface them.
      return thunkAPI.rejectWithValue(
        err?.message ? `Failed to add customer: ${err.message}` : "Failed to add customer. Please try again."
      );
    }
  }
);

export const sendPasswordReset = createAsyncThunk(
  "admin/sendPasswordReset",
  async (email, thunkAPI) => {
    try {
      const sendPasswordResetFunction = httpsCallable(
        functions,
        "adminDashboardF-sendPasswordResetEmail"
      );
      const result = await sendPasswordResetFunction({ customerEmail: email });
      if (result.data.success) {
        alert(`Password reset email sent to ${email}`);
        return email;
      }
      return thunkAPI.rejectWithValue(
        "Failed to send password reset email. Please try again."
      );
    } catch (err) {
      console.error("Error sending password reset email: ", err);
      return thunkAPI.rejectWithValue(
        "Failed to send password reset email. Please try again."
      );
    }
  }
);

export const editCustomer = createAsyncThunk(
  "admin/editCustomer",
  async ({ customerId, updatedData }, thunkAPI) => {
    try {
      const editCustomerFunction = httpsCallable(functions, "adminDashboardF-editCustomer");
      const response = await editCustomerFunction({ customerId, updatedData });
      if (response.data.success) {
        return { customerId, updatedData };
      }
      return thunkAPI.rejectWithValue("Failed to save. Please try again.");
    } catch (error) {
      console.error("Error editing customer: ", error);
      // Callable HttpsError messages are authored server-side (the
      // whitelist/validation/Auth-sync errors in adminDashboardF-editCustomer)
      // - surface them like createCustomer does above.
      return thunkAPI.rejectWithValue(
        error?.message ? `Failed to save: ${error.message}` : "Failed to edit customer. Please try again."
      );
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "admin/deleteCustomer",
  async (customerId, thunkAPI) => {
    try {
      const deleteCustomerFunction = httpsCallable(functions, "adminDashboardF-deleteCustomer");
      const response = await deleteCustomerFunction({ customerId });
      if (response.data.success) {
        return customerId;
      }
      return thunkAPI.rejectWithValue("Failed to delete. Please try again.");
    } catch (error) {
      console.error("Error deleting customer: ", error);
      // Callable HttpsError messages are authored server-side - surface
      // them like createCustomer does above.
      return thunkAPI.rejectWithValue(
        error?.message ? `Failed to delete: ${error.message}` : "Failed to delete customer. Please try again."
      );
    }
  }
);

// Writes customers/{cid}.modules (the per-customer "Modules" panel in the
// Customers tab). ONLY adminDashboardF-setCustomerModules is allowed to
// write that field - never fold this into editCustomer's payload.
// pending/rejected are deliberately NOT wired into extraReducers below:
// every other thunk here shares the single state.status flag, and
// AdminDashboard.jsx full-page-blanks on status === 'loading'
// ("Loading dashboard..."), which would hide the modal itself mid-save.
// CustomerModulesModal tracks its own local isSaving/error via
// dispatch(...).unwrap() instead. fulfilled IS wired (see below) purely to
// patch state.customers locally, the same way editCustomer.fulfilled does -
// CustomerModulesModal also dispatches fetchCustomers() on success for
// cross-admin consistency, but that alone isn't enough: it reads
// metadata/customers.lastUpdated, which onCustomerChange bumps
// ASYNCHRONOUSLY after the write, so a fetchCustomers() fired immediately
// after save can occasionally lose that race and see a stale timestamp
// (returning customers: null). The local patch here makes the just-saved
// change visible immediately regardless of that race.
export const setCustomerModules = createAsyncThunk(
  "admin/setCustomerModules",
  async ({ customerId, modules }, thunkAPI) => {
    try {
      const setCustomerModulesFunction = httpsCallable(
        functions,
        "adminDashboardF-setCustomerModules"
      );
      const result = await setCustomerModulesFunction({ customerId, modules });
      if (result.data.success) {
        return { customerId, modules };
      }
      return thunkAPI.rejectWithValue("Failed to update modules. Please try again.");
    } catch (err) {
      console.error("Error setting customer modules: ", err);
      return thunkAPI.rejectWithValue(
        "Failed to update modules. Please try again."
      );
    }
  }
);

// Writes customers/{cid}.locationPingIntervalMinutes - the PLATFORM-owned GPS
// ping cadence surfaced as the "Tracking" card in the customer detail
// drawer. Customers never see or control this value; only
// adminDashboardF-setCustomerLocationPing (admin-gated) may write it.
// Modelled on setCustomerModules above (same httpsCallable/success/
// rejectWithValue shape), but deliberately has NO matching extraReducers
// case: unlike setCustomerModules there is no local optimistic patch of
// state.customers here - the drawer dispatches fetchCustomers() after a
// successful save (the same cross-admin refresh CustomerModulesModal does)
// and reads locationPingIntervalMinutes/locationPingUpdatedAt straight off
// the refetched customer doc.
export const setCustomerLocationPing = createAsyncThunk(
  "admin/setCustomerLocationPing",
  async ({ customerId, locationPingIntervalMinutes }, thunkAPI) => {
    try {
      const setCustomerLocationPingFunction = httpsCallable(
        functions,
        "adminDashboardF-setCustomerLocationPing"
      );
      const result = await setCustomerLocationPingFunction({
        customerId,
        locationPingIntervalMinutes,
      });
      if (result.data.success) {
        return {
          customerId,
          locationPingIntervalMinutes: result.data.locationPingIntervalMinutes,
        };
      }
      return thunkAPI.rejectWithValue(
        "Failed to update location ping interval. Please try again."
      );
    } catch (err) {
      console.error("Error setting customer location ping interval: ", err);
      return thunkAPI.rejectWithValue(
        "Failed to update location ping interval. Please try again."
      );
    }
  }
);

// Fleet device CAN-odometer round (2026-09-24): the Devices tab in the
// customer detail drawer. `fetchCustomerFleetDevices` lists the platform's
// `fleetDevices/{imei}` rows for one tenant (Vehicle/Equipment tracker
// allowlist); `setCustomerFleetDeviceCan` flips the per-device `canOdometer`
// flag (whether that specific tracker has a CAN bus - the `web.fleetCan`
// tenant module is the BILLING gate, this is which devices actually use it).
// Modelled on setCustomerModules/setCustomerLocationPing above, but state
// lives in its own `fleetDevicesByCustomer` map keyed by customerId (never
// `state.customers`) so a panel can only ever render the list for the
// customer currently open. Like setCustomerModules, `state.error` is never
// touched - the panel shows messages inline (see CustomerFleetDevicesPanel).
export const fetchCustomerFleetDevices = createAsyncThunk(
  "admin/fetchCustomerFleetDevices",
  async (customerId, thunkAPI) => {
    try {
      const listCustomerFleetDevicesFunction = httpsCallable(
        functions,
        "adminDashboardF-listCustomerFleetDevices"
      );
      const result = await listCustomerFleetDevicesFunction({ customerId });
      return { customerId, devices: result.data.devices || [] };
    } catch (err) {
      console.error("Error fetching customer fleet devices: ", err);
      return thunkAPI.rejectWithValue({
        customerId,
        message:
          err?.message || "Failed to load devices. Please try again.",
      });
    }
  }
);

export const setCustomerFleetDeviceCan = createAsyncThunk(
  "admin/setCustomerFleetDeviceCan",
  async ({ customerId, imei, canOdometer }, thunkAPI) => {
    try {
      const setCustomerFleetDeviceCanFunction = httpsCallable(
        functions,
        "adminDashboardF-setCustomerFleetDeviceCan"
      );
      const result = await setCustomerFleetDeviceCanFunction({
        customerId,
        imei,
        canOdometer,
      });
      if (result.data.success) {
        return {
          customerId,
          imei: result.data.imei,
          canOdometer: result.data.canOdometer,
        };
      }
      return thunkAPI.rejectWithValue(
        "Failed to update device. Please try again."
      );
    } catch (err) {
      console.error("Error setting fleet device CAN odometer flag: ", err);
      return thunkAPI.rejectWithValue(
        err?.message
          ? `Failed to update device: ${err.message}`
          : "Failed to update device. Please try again."
      );
    }
  }
);

// Ops (2026-08-31): Firebase usage/health round. Both thunks below are pure
// reads consumed directly by their calling component via dispatch(...).unwrap()
// - same reasoning as setCustomerModules above (see the comment there): the
// shared state.status flag full-page-blanks AdminDashboard.jsx
// ("Loading dashboard...") whenever it flips to 'loading', so wiring these
// into extraReducers would hide the Dashboard/Customers UI every time the
// Security card or a Data-preview Load click fires. Neither of these
// mutates state.customers either, so - unlike setCustomerModules - there is
// no .fulfilled case to add here at all: the calling component owns 100% of
// the result via local state.
export const getOpsOverview = createAsyncThunk(
  "admin/getOpsOverview",
  async (_, thunkAPI) => {
    try {
      const getOpsOverviewFunction = httpsCallable(
        functions,
        "adminDashboardF-getOpsOverview"
      );
      const result = await getOpsOverviewFunction();
      return result.data; // { securitySweep }
    } catch (err) {
      console.error("Error fetching ops overview: ", err);
      return thunkAPI.rejectWithValue(
        "Failed to load ops overview. Please try again."
      );
    }
  }
);

export const getCustomerDataPreview = createAsyncThunk(
  "admin/getCustomerDataPreview",
  async ({ customerId, collection, limit }, thunkAPI) => {
    try {
      const getCustomerDataPreviewFunction = httpsCallable(
        functions,
        "adminDashboardF-getCustomerDataPreview"
      );
      const result = await getCustomerDataPreviewFunction({
        customerId,
        collection,
        limit,
      });
      return result.data; // { customerId, collection, docs }
    } catch (err) {
      console.error("Error fetching customer data preview: ", err);
      return thunkAPI.rejectWithValue(
        "Failed to load data preview. Please try again."
      );
    }
  }
);

export const fetchCustomers = createAsyncThunk(
    'admin/fetchCustomers',
    async (_, thunkAPI) => {
      try {
        const state = thunkAPI.getState();
        const lastFetchTimestamp = state.admin.lastFetchTimestamp;
  
        const fetchCustomersFunction = httpsCallable(functions, 'adminDashboardF-fetchCustomers');
        const response = await fetchCustomersFunction({ lastFetchTimestamp });
        return { 
          customers: response.data.customers, 
          lastUpdated: response.data.lastUpdated 
        };
      } catch (error) {
        console.error("Error fetching customers: ", error);
        return thunkAPI.rejectWithValue("Failed to fetch customers. Please try again.");
      }
    }
  );
  
  const adminSlice = createSlice({
    name: "admin",
    initialState: {
      customers: [],
      error: null,
      status: "idle",
      lastFetchTimestamp: null,
      // Devices tab (2026-09-24): { [customerId]: {items: [], loading: false,
      // error: null} }. Keyed by customerId so a panel can only ever render
      // the current customer's own devices - never a shared/global list.
      fleetDevicesByCustomer: {},
    },
  reducers: {
    // Round 4: editCustomer.rejected/deleteCustomer.rejected surface inline
    // in the detail drawer and no longer touch state.error (the full-width
    // red banner used to duplicate the drawer's own message, half hidden
    // behind its backdrop). state.error is still set by fetchCustomers and
    // createCustomer; AdminDashboard.jsx dispatches this on every tab
    // change so a failed fetch doesn't leave a permanent banner behind
    // while the admin works elsewhere.
    clearAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // createCustomer: only .fulfilled (no .pending/.rejected - the shared
      // state.status flag would blank the whole dashboard, see the
      // setCustomerModules comment above). Appends the new row so it shows
      // without a reload; a later fetchCustomers replaces the list anyway.
      .addCase(createCustomer.fulfilled, (state, action) => {
        const created = action.payload;
        if (created?.id && !state.customers.some((c) => c.id === created.id)) {
          state.customers.push(created);
        }
      })
      .addCase(editCustomer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.customers.findIndex(
          (customer) => customer.id === action.payload.customerId
        );
        if (index !== -1) {
          // Mirror the server: adminDashboardF-editCustomer also writes
          // companyName whenever name changes (the office app's company
          // label reads companyName first - see customerFormat.displayName),
          // so patch it here too or the table/drawer/Overview keep showing
          // the OLD company label until the next fetchCustomers.
          const patch = { ...action.payload.updatedData };
          if (patch.name !== undefined) {
            patch.companyName = patch.name;
          }
          state.customers[index] = {
            ...state.customers[index],
            ...patch,
          };
        }
        state.error = null;
      })
      .addCase(editCustomer.rejected, (state) => {
        // The drawer shows action.payload inline (unwrap) - see reducers.
        state.status = "failed";
      })
      .addCase(deleteCustomer.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.customers = state.customers.filter(
          (customer) => customer.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteCustomer.rejected, (state) => {
        // The drawer's Danger zone shows action.payload inline (unwrap).
        state.status = "failed";
      })

      // Deliberately only .fulfilled - no .pending/.rejected - see the
      // comment above the setCustomerModules thunk for why state.status
      // stays untouched here.
      .addCase(setCustomerModules.fulfilled, (state, action) => {
        const index = state.customers.findIndex(
          (customer) => customer.id === action.payload.customerId
        );
        if (index !== -1) {
          state.customers[index] = {
            ...state.customers[index],
            modules: action.payload.modules,
          };
        }
      })

      // Devices tab (2026-09-24). Own per-customer loading/error, deliberately
      // NOT the shared state.status flag (same reasoning as
      // getOpsOverview/getCustomerDataPreview above - that flag full-page-
      // blanks AdminDashboard.jsx, which would hide the whole drawer while a
      // Devices tab is loading).
      .addCase(fetchCustomerFleetDevices.pending, (state, action) => {
        const customerId = action.meta.arg;
        const existing =
          state.fleetDevicesByCustomer[customerId] || { items: [], loading: false, error: null };
        state.fleetDevicesByCustomer[customerId] = {
          ...existing,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchCustomerFleetDevices.fulfilled, (state, action) => {
        const { customerId, devices } = action.payload;
        state.fleetDevicesByCustomer[customerId] = {
          items: devices,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchCustomerFleetDevices.rejected, (state, action) => {
        const customerId = action.payload?.customerId ?? action.meta.arg;
        const existing =
          state.fleetDevicesByCustomer[customerId] || { items: [], loading: false, error: null };
        state.fleetDevicesByCustomer[customerId] = {
          ...existing,
          loading: false,
          error: action.payload?.message || "Failed to load devices. Please try again.",
        };
      })

      // Deliberately only .fulfilled - like setCustomerModules above, the
      // Devices panel tracks its own in-flight save locally (async button
      // rule) and shows a failure message inline itself via unwrap().
      .addCase(setCustomerFleetDeviceCan.fulfilled, (state, action) => {
        const { customerId, imei, canOdometer } = action.payload;
        const bucket = state.fleetDevicesByCustomer[customerId];
        if (bucket) {
          const index = bucket.items.findIndex((device) => device.imei === imei);
          if (index !== -1) {
            bucket.items[index] = { ...bucket.items[index], canOdometer };
          }
        }
      })

      .addCase(fetchCustomers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.customers !== null) {
          state.customers = action.payload.customers;
        }
        state.lastFetchTimestamp = action.payload.lastUpdated;
        state.error = null;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearAdminError } = adminSlice.actions;

export const selectAllCustomers = (state) => state.admin.customers;
export const selectAdminError = (state) => state.admin.error;
export const selectAdminStatus = (state) => state.admin.status;
export const selectLastFetchTimestamp = (state) =>
  state.admin.lastFetchTimestamp;

// Devices tab (2026-09-24): always returns a concrete {items, loading,
// error} shape (never undefined) so CustomerFleetDevicesPanel can render
// without a defensive null-check at every call site.
export const selectFleetDevicesForCustomer = (state, customerId) =>
  state.admin.fleetDevicesByCustomer[customerId] || {
    items: [],
    loading: false,
    error: null,
  };

export default adminSlice.reducer;
